import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'

// antd
import { Typography, Upload, message, theme, Tooltip } from 'antd'
import { PlusOutlined } from '@ant-design/icons'

// assets

// slices
import {
    getS3UrlForAttachment,
    mainTaskUpdate,
    taskFileError,
    taskFilesRemove,
    taskFilesUpload,
    tasksState,
    taskUpdateSupport,
} from '../../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    isTaskMutationSuccess,
} from '../../taskRefactored/tasks.service'

// constants
import { fileTypes, pdfTypes, videoTypes } from '../../../constants/mediaTypes'

import dayjs from 'dayjs'

// styles
import styles from '../../taskRefactored/tasks.module.css'
import sectionStyles from './ProjectAttachmentSection.module.css'
import { uploadAttachmentToAWS } from '../../taskRefactored/tasks.service'
import MediaViewer from '../../taskRefactored/components/mediaViewer'
import {
    confirmOversizedUpload,
    MAX_ATTACHMENT_SIZE_MB,
} from './confirmOversizedUpload'
import { showActionNotification } from '../../../components/notification'

const ProjectAttachmentSection = ({
    taskId,
    onTaskUpdate,
    taskListKind = 'support',
    readOnly = false,
}) => {
    const isMainTask = taskListKind === 'main'
    const { Text } = Typography

    const { useToken } = theme
    const { token } = useToken()

    const dispatch = useDispatch()

    const { taskFiles, fileError } = useSelector(tasksState)
    const { selectedTask } = useSelector(tasksState)

    const [stream, setStream] = useState(false)
    const [fileURL, setFileURL] = useState([])
    const fileURLRef = useRef([])
    const uploadTrackingRef = useRef({
        pending: new Set(),
        completed: [],
    })
    const oversizedBatchRef = useRef({
        names: [],
        uploadingCount: 0,
        timer: null,
    })

    useEffect(() => {
        fileURLRef.current = fileURL
    }, [fileURL])

    useEffect(() => {
        if (selectedTask?.data?.attachment) {
            const attachments = selectedTask?.data?.attachment || []
            dispatch(taskFilesRemove(attachments))
            setFileURL(attachments)
        } else {
            dispatch(taskFilesRemove([]))
            setFileURL([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTask?.data?.attachment, taskId])

    useEffect(() => {
        const oversizedBatch = oversizedBatchRef.current
        return () => {
            if (oversizedBatch.timer) {
                clearTimeout(oversizedBatch.timer)
            }
        }
    }, [])

    const uploadButton = (
        <div>
            <PlusOutlined />
        </div>
    )

    // UPLOADING BUTTON
    const uploadButtonLoading = (
        <div
            className={styles.uploadButtonBox}
            style={{
                border: '1px solid #edeae9',
            }}
        >
            <Icon
                icon="line-md:uploading-loop"
                width={26}
                height={26}
                color={token.colorPalette.baseColor.tertiary}
            />
        </div>
    )

    const persistAttachments = async (allAttachments) => {
        if (!taskId || !selectedTask?.data) return false

        if (isMainTask) {
            const task = selectedTask.data
            const body = buildMainTaskUpdateBodyFromTask(
                task,
                task?.projectId,
                { attachment: allAttachments }
            )
            const result = await dispatch(mainTaskUpdate({ id: taskId, body }))
            return isTaskMutationSuccess(result?.payload?.data)
        }

        const payload = {
            _id: taskId,
            updateTask: {
                title: selectedTask?.data?.title || '',
                projectId: selectedTask?.data?.projectId || null,
                type: selectedTask?.data?.type || 'Task',
                assigneeId: selectedTask?.data?.assigneeId?._id || null,
                priority: selectedTask?.data?.priority || 'Low',
                status: selectedTask?.data?.status || 'To Do',
                description: selectedTask?.data?.description || '',
                start: selectedTask?.data?.start || null,
                end: selectedTask?.data?.end || null,
                attachment: allAttachments,
            },
        }
        const result = await dispatch(taskUpdateSupport(payload))
        return Boolean(result?.payload?.data?.success)
    }

    const clearStreamIfIdle = () => {
        if (uploadTrackingRef.current.pending.size === 0) {
            setStream(false)
        }
    }

    const handleAllUploadsComplete = async () => {
        if (
            uploadTrackingRef.current.pending.size !== 0 ||
            uploadTrackingRef.current.completed.length === 0
        ) {
            return
        }

        const completed = [...uploadTrackingRef.current.completed]
        uploadTrackingRef.current.completed = []

        const allAttachments = [...fileURLRef.current, ...completed]
        fileURLRef.current = allAttachments
        setFileURL(allAttachments)
        completed.forEach((link) => {
            dispatch(taskFilesUpload(link))
        })

        // Keep loader until files are in the list, then clear.
        clearStreamIfIdle()

        if (taskId && selectedTask?.data) {
            try {
                const ok = await persistAttachments(allAttachments)
                if (ok) {
                    onTaskUpdate?.()
                } else {
                    message.error('Failed to update task attachments')
                }
            } catch {
                message.error('Failed to update task attachments')
            }
        }
    }

    const props = {
        name: 'files',
        multiple: true,
        async onChange(info) {
            if (fileError?.length === 0) {
                const fileId = `${info?.file?.name}_${dayjs(new Date()).unix()}`
                uploadTrackingRef.current.pending.add(fileId)
                setStream(true)

                const nameChange = info?.file?.name
                    .split('.')[0]
                    .concat(`_${dayjs(new Date()).unix()}`)
                    .concat(
                        `.${
                            info?.file?.name.split('.')[
                                info?.file?.name.split('.')?.length - 1
                            ]
                        }`
                    )

                window.Buffer = window.Buffer || require('buffer').Buffer

                const formData = {
                    data: [
                        {
                            filename: nameChange,
                            contentType: info?.file?.type,
                        },
                    ],
                }

                dispatch(getS3UrlForAttachment(formData))
                    .then(async (data) => {
                        if (data?.payload?.data?.success) {
                            const response = data?.payload?.data?.data
                            const uploadUrl = response?.[0]?.uploadUrl
                            const publicLink = response?.[0]?.publicLink

                            if (uploadUrl) {
                                const formData = {
                                    url: uploadUrl,
                                    data: info?.file,
                                }
                                await uploadAttachmentToAWS(formData)

                                // Mark this file as completed
                                uploadTrackingRef.current.pending.delete(fileId)
                                uploadTrackingRef.current.completed.push(
                                    publicLink
                                )

                                // Keep loader until files are added to the list
                                if (
                                    uploadTrackingRef.current.pending.size === 0
                                ) {
                                    await handleAllUploadsComplete()
                                }
                            } else {
                                uploadTrackingRef.current.pending.delete(fileId)
                                message.error(
                                    'Upload Failed!. Please Upload again'
                                )
                                clearStreamIfIdle()
                            }
                        } else {
                            uploadTrackingRef.current.pending.delete(fileId)
                            message.error('Upload Failed!. Please Upload again')
                            clearStreamIfIdle()
                        }
                    })
                    .catch(() => {
                        uploadTrackingRef.current.pending.delete(fileId)
                        message.error('Upload Failed!. Please Upload again')
                        clearStreamIfIdle()
                    })
            }
        },
        beforeUpload(file, fileList) {
            dispatch(taskFileError(''))

            const isWithinLimit =
                file.size / 1024 / 1024 <= MAX_ATTACHMENT_SIZE_MB
            if (!isWithinLimit) {
                const batch = oversizedBatchRef.current
                const selectedCount = Array.isArray(fileList)
                    ? fileList.length
                    : 1
                batch.uploadingCount = Math.max(
                    batch.uploadingCount || 0,
                    selectedCount
                )
                if (file?.name && !batch.names.includes(file.name)) {
                    batch.names.push(file.name)
                }
                if (batch.timer) clearTimeout(batch.timer)
                batch.timer = setTimeout(() => {
                    const rejectedNames = [...batch.names]
                    const uploadingCount = batch.uploadingCount
                    batch.names = []
                    batch.uploadingCount = 0
                    batch.timer = null
                    if (rejectedNames.length > 0) {
                        confirmOversizedUpload({
                            fileNames: rejectedNames,
                            uploadingCount,
                            maxSizeMb: MAX_ATTACHMENT_SIZE_MB,
                        })
                    }
                }, 0)
                return Upload.LIST_IGNORE
            }

            return false
        },
        onDrop() {},
    }

    const handleDelete = async (file) => {
        const removeData = [...fileURL]
        removeData.splice(
            removeData.findIndex((item) => item === file),
            1
        )
        setFileURL(removeData)
        dispatch(taskFilesRemove(removeData))

        if (taskId && selectedTask?.data) {
            const ok = await persistAttachments(removeData)
            if (ok) {
                showActionNotification({
                    type: 'success',
                    message: 'Attachment deleted',
                    notificationKey: 'attachment-deleted',
                })
                onTaskUpdate?.()
            } else {
                message.error('Failed to update task attachments')
            }
        } else {
            showActionNotification({
                message: 'Attachment deleted',
                notificationKey: 'attachment-deleted',
            })
        }
    }

    return (
        <>
            <div
                style={{
                    padding: '0px 25px',
                }}
            >
                <div
                    style={{
                        marginTop: '20px',
                    }}
                >
                    <div className={sectionStyles.attachmentHeader}>
                        <Text className={sectionStyles.attachmentTitle}>
                            Attachments
                        </Text>
                        {Array.isArray(taskFiles) && taskFiles.length > 0 ? (
                            <span
                                className={sectionStyles.attachmentCountBadge}
                            >
                                {taskFiles.length}
                            </span>
                        ) : null}
                        {!readOnly ? (
                            <Upload
                                accept={[
                                    ...fileTypes,
                                    ...videoTypes,
                                    ...pdfTypes,
                                ]}
                                {...props}
                                fileList={[]}
                                showUploadList={false}
                                className={sectionStyles.attachmentHeaderUpload}
                            >
                                <Tooltip title="Add attachment" placement="top">
                                    <button
                                        type="button"
                                        className={
                                            sectionStyles.attachmentAddIcon
                                        }
                                        aria-label="Add attachment"
                                    >
                                        <Icon
                                            icon="mdi:plus"
                                            width={17}
                                            height={17}
                                        />
                                    </button>
                                </Tooltip>
                            </Upload>
                        ) : null}
                    </div>
                    <div
                        className={`${sectionStyles.attachmentFiles} fileTask`}
                    >
                        {stream && uploadButtonLoading}
                        {taskFiles?.length > 0 && (
                            <MediaViewer
                                mediaFiles={taskFiles}
                                handleDelete={
                                    readOnly ? undefined : handleDelete
                                }
                            />
                        )}
                        {!readOnly && taskFiles?.length > 0 && (
                            <Upload
                                listType="picture-card"
                                accept={[
                                    ...fileTypes,
                                    ...videoTypes,
                                    ...pdfTypes,
                                ]}
                                {...props}
                                fileList={[]}
                                rootClassName={styles.taskUploadAttachment}
                                className={`attachmentUploadWrapper ${sectionStyles.attachmentUploadCard}`}
                            >
                                {uploadButton}
                            </Upload>
                        )}
                    </div>
                    {fileError &&
                        (typeof fileError === 'string'
                            ? fileError.length > 0
                            : fileError?.error || fileError?.message) && (
                            <span style={{ color: 'red', margin: 2 }}>
                                {typeof fileError === 'string'
                                    ? fileError
                                    : fileError?.error ||
                                      fileError?.message ||
                                      JSON.stringify(fileError)}
                            </span>
                        )}
                </div>
            </div>
        </>
    )
}

export default ProjectAttachmentSection
